import { invocationTypeForStoredValue, invocationTypeLabel } from '../../../lib/mcpAnalytics/invocationTypes'

/* ─── MCP Analytics cell badges ───

   Every label rendered here is **product vocabulary and is never translated**
   (spec §4.7-22). These are data-column values, and the server orders the tables
   by these exact English strings (`SORT_EXPRESSIONS` in
   mcp_analytics_repository.py) — translating them would make the rendered order
   and the requested order disagree in every non-English locale. Chrome around the
   table (column headings, empty states, the pager) *is* localised. */

export const CHANNEL_LABELS = { agent: 'Studio Agent', external: 'External Client' } as const
export const MODE_LABELS = { single: 'Single', batch: 'Batch' } as const
export const STATUS_LABELS = { ok: '200 OK', error: 'Error' } as const

/**
 * The badge plus, when the call came through a Studio agent, `· <agent name>`.
 *
 * `agentName` is an empty string on every row until mmcp lands MS-697, so the
 * suffix is absent today and appears with no further change once the emitter
 * populates the field. An external client never has one by definition.
 */
export function ChannelBadge({ channel, agentName }: { channel: string; agentName?: string }) {
  const isAgent = channel === 'agent'
  return (
    <>
      <span className={isAgent ? 'badge badge-ch-agent' : 'badge badge-ch-ext'}>
        {isAgent ? CHANNEL_LABELS.agent : CHANNEL_LABELS.external}
      </span>
      {isAgent && agentName ? (
        <>
          <span className="an-sep"> · </span>
          <span className="an-agent">{agentName}</span>
        </>
      ) : null}
    </>
  )
}

export function InvocationModeBadge({ mode }: { mode: string }) {
  const isBatch = mode === 'batch'
  return (
    <span className={isBatch ? 'badge badge-call-batch' : 'badge badge-call-single'}>
      {isBatch ? MODE_LABELS.batch : MODE_LABELS.single}
    </span>
  )
}

/** Outlined in the type's own colour, which is the same token the chart segment and
 *  the legend swatch use — one source, so a recoloured type recolours everywhere. */
export function InvocationTypeBadge({ invocationType }: { invocationType: string }) {
  const entry = invocationTypeForStoredValue(invocationType)
  const color = entry?.colorVar ?? 'var(--text-tertiary)'
  return (
    <span
      className="badge"
      style={{ background: 'transparent', border: `1px solid ${color}`, color }}
    >
      {invocationTypeLabel(invocationType)}
    </span>
  )
}

export function ResponseStatusBadge({ status }: { status: string }) {
  const isOk = status === 'ok'
  return (
    <span className={isOk ? 'badge badge-read' : 'badge badge-execute'}>
      {isOk ? STATUS_LABELS.ok : STATUS_LABELS.error}
    </span>
  )
}

/** A data cell rendered as content rather than as an identifier to copy — the
 *  interface font, matching the fixed "Pinging Server" entry beside it. Falls back
 *  to an em dash so an empty cell still occupies its row. */
export function DataCell({ value }: { value: string }) {
  return value ? <span className="an-data">{value}</span> : <>—</>
}
